/* src/features/5DD/DayView.js */
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../apiClient';
import { format, startOfDay, endOfDay } from 'date-fns';
import { calendarService } from '../../services/google/calendar';
import SleepModal from './SleepModal'; 

// --- STYLES ---
const styles = {
  dayView: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    padding: '10px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    color: '#fff',
  },
  section: {
    width: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '8px',
    padding: '15px',
    boxSizing: 'border-box',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    margin: '0 0 10px 0',
    borderBottom: '1px solid #555',
    paddingBottom: '5px',
    color: '#fff',
  },
  buttonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    alignItems: 'stretch',
  },
  logButton: {
    padding: '12px',
    fontSize: '1em',
    border: '1px solid #ccc',
    borderRadius: '6px',
    background: '#f0f0f0',
    color: '#333',
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.2s',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hh5Button: {
    padding: '12px',
    fontSize: '1em',
    border: '1px solid #f0f0f0',
    borderRadius: '6px',
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    width: '100%',
    margin: '5px 0',
    opacity: 1,
    transition: 'opacity 0.2s',
    fontWeight: 'bold', 
  },
  logButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    background: '#555',
    color: '#aaa',
    border: '1px solid #555',
  },
  hh5ButtonCompleted: {
    border: '1px solid #4caf50',
    background: 'rgba(76, 175, 80, 0.2)',
    color: '#e8f5e9',
  },
  eventList: {
    maxHeight: '200px',
    overflowY: 'auto',
  },
  eventItem: {
    fontSize: '0.9em',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid #555',
    padding: '5px 8px',
    margin: '4px 0',
    borderRadius: '4px',
    color: '#fff',
  }
};

// --- HELPERS ---

function calculateProgress(tasks) {
  if (!tasks) return 0; 
  let score = 0;
  if (tasks.completed_hh5_am) score++;
  if (tasks.completed_hh5_pm) score++;
  if (tasks.completed_hh5_night) score++;
  if (tasks.completed_journal) score++;
  if (tasks.logged_meal) score++;
  if (tasks.logged_workout) score++;
  if (tasks.logged_sleep) score++;
  if (tasks.logged_emotions) score++;
  
  if (score === 0) return 0; 
  if (score <= 2) return 1;  
  if (score <= 4) return 2;  
  if (score <= 6) return 3;  
  if (score <= 8) return 4;  
  if (score >= 9) return 5;  
  return 0;
}

function getStyleForProgress(score) {
  const styles = [
    { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: '#fff' },
    { backgroundColor: 'rgba(1, 87, 155, 0.3)', color: '#fff' },
    { backgroundColor: 'rgba(2, 119, 189, 0.4)', color: '#fff' },
    { backgroundColor: 'rgba(27, 94, 32, 0.4)', color: '#fff' },
    { backgroundColor: 'rgba(56, 142, 60, 0.5)', color: '#fff' },
    { backgroundColor: 'rgba(245, 127, 23, 0.5)', color: '#fff', border: '2px solid #FFC107' },
  ];
  return styles[score] || styles[0];
}


// --- MAIN COMPONENT ---

function DayView({ session, day, dayIndex, navigateTo }) {
  const [tasks, setTasks] = useState(null);
  const [mealCount, setMealCount] = useState(0); 
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sleepModalOpen, setSleepModalOpen] = useState(false);

  // NOTE: MealLogger import and state removed. We now navigate instead.

  const providerToken = session.provider_token || localStorage.getItem('google_provider_token');
  const taskDate = format(day, 'yyyy-MM-dd');

  const fetchMealCount = useCallback(async () => {
    try {
      const data = await db.meals.list({ date_logged: taskDate });
      setMealCount(Array.isArray(data) ? data.length : 0);
    } catch (error) {
      console.error("Error fetching meal count:", error.message);
      setMealCount(0);
    }
  }, [session.user.id, taskDate]);


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Tasks
      const tasksData = await db.tasks.list({ task_date: taskDate });
      const taskData = Array.isArray(tasksData) ? tasksData[0] : tasksData;
      setTasks(taskData || null);
      
      // 2. Fetch Meal Count
      await fetchMealCount();

      // 3. Fetch Calendar
      if (providerToken) {
        const startDate = startOfDay(day);
        const endDate = endOfDay(day);
        const events = await calendarService.listEvents(providerToken, startDate, endDate);
        setCalendarEvents(events);
      }
    } catch (error) {
      console.error('Error fetching day data:', error.message);
    } finally {
      setLoading(false);
    }
  }, [session.user.id, taskDate, day, providerToken, fetchMealCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- LOGGING FUNCTIONS ---
  
  const logTask = async (taskName) => {
    try {
      const data = await db.tasks.upsert({
        task_date: taskDate,
        [taskName]: true,
      });
      setTasks(data);
    } catch (error) {
      console.error(`Error logging ${taskName}:`, error.message);
    }
  };

  const handleSleepConfirm = async (rating, tags) => {
    try {
      const data = await db.tasks.upsert({
        task_date: taskDate,
        logged_sleep: true,
        sleep_quality: rating,
        sleep_tags: tags,
      });
      setTasks(data);
    } catch (error) {
      console.error('Error logging sleep:', error.message);
    }
    setSleepModalOpen(false);
  };

  const handleStartHH5 = async (timeOfDay) => {
    const taskColumn = `completed_hh5_${timeOfDay.toLowerCase()}`;
    const isAlreadyDone = tasks && tasks[taskColumn];

    if (!isAlreadyDone) {
        try {
            if(providerToken) {
                const title = `HH5 Session - ${timeOfDay}`;
                const startTime = new Date(); 
                const endTime = new Date(startTime.getTime() + 5 * 60000); 
                const event = {
                    'summary': title,
                    'start': { 'dateTime': startTime.toISOString() },
                    'end': { 'dateTime': endTime.toISOString() },
                };
                await calendarService.createEvent(providerToken, event);
            }
            await logTask(taskColumn);
        } catch (error) {
            console.error('Error logging HH5 session:', error.message);
        }
    }
    window.location.href = '/FIVE/';
  };
  
  if (loading) {
    return <div>Loading Day {dayIndex}...</div>
  }

  const progressScore = calculateProgress(tasks);
  const dayStyle = getStyleForProgress(progressScore);
  
  const getBtnStyle = (isDone) => ({
    ...styles.logButton,
    ...(isDone ? styles.logButtonDisabled : {}),
  });
  
  const getHH5BtnStyle = (isDone) => ({
    ...styles.hh5Button,
    ...(isDone ? styles.hh5ButtonCompleted : {}),
  });

  return (
    <>
      <SleepModal
        isOpen={sleepModalOpen}
        onClose={() => setSleepModalOpen(false)}
        onConfirm={handleSleepConfirm}
        initialRating={tasks?.sleep_quality}
        initialTags={tasks?.sleep_tags}
      />

      <div style={{ ...styles.dayView, ...dayStyle }}>
        
        {/* --- Section 1: HH5 Tasks --- */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>HH5 Practice</h4>
          {dayIndex === 0 && (
            <button 
              style={getHH5BtnStyle(tasks?.completed_hh5_night)}
              onClick={() => handleStartHH5('Night')}
            >
              {tasks?.completed_hh5_night ? '✅ Start 8:20 PM Session' : 'Start 8:20 PM Session'}
            </button>
          )}
          {dayIndex > 0 && (
            <>
              <button 
                style={getHH5BtnStyle(tasks?.completed_hh5_am)}
                onClick={() => handleStartHH5('AM')}
              >
                {tasks?.completed_hh5_am ? '✅ Start 8:20 AM Session' : 'Start 8:20 AM Session'}
              </button>
              <button 
                style={getHH5BtnStyle(tasks?.completed_hh5_pm)}
                onClick={() => handleStartHH5('PM')}
              >
                {tasks?.completed_hh5_pm ? '✅ Start 4:20 PM Session' : 'Start 4:20 PM Session'}
              </button>
              <button 
                style={getHH5BtnStyle(tasks?.completed_hh5_night)}
                onClick={() => handleStartHH5('Night')}
              >
                {tasks?.completed_hh5_night ? '✅ Start 8:20 PM Session' : 'Start 8:20 PM Session'}
              </button>
            </>
          )}
        </div>

        {/* --- Section 2: Daily Log --- */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Daily Log</h4>
          <div style={styles.buttonGrid}>
            
			{/* Correct Button Code */}
			<button 
				style={getBtnStyle(false)} 
				onClick={() => {
					console.log("Meals button clicked!"); // Optional debug
					navigateTo('nutrition');
				}} 
			>
				{mealCount > 0 ? `Meals (${mealCount})` : 'Meals'}
			</button>
            {/* ---------------------- */}

            <button style={getBtnStyle(tasks?.logged_workout)} onClick={() => logTask('logged_workout')} disabled={tasks?.logged_workout}>
                {tasks?.logged_workout ? '✅ Workout' : 'Workout'}
            </button>
            <button style={getBtnStyle(tasks?.logged_sleep)} onClick={() => setSleepModalOpen(true)}>
                {tasks?.logged_sleep ? '✅ Sleep' : 'Sleep'}
            </button>
            <button style={getBtnStyle(tasks?.logged_emotions)} onClick={() => logTask('logged_emotions')} disabled={tasks?.logged_emotions}>
                {tasks?.logged_emotions ? '✅ Emotion' : 'Emotion'}
            </button>
          </div>
        </div>
        
        {/* --- Section 3: Journal --- */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Journal</h4>
          <button 
            style={{...styles.logButton, width: '100%'}} 
            onClick={() => navigateTo('journal')}
          >
            {tasks?.completed_journal ? '✅ Go to Journal' : 'Go to Journal'}
          </button>
        </div>

        {/* --- Section 4: Calendar Events --- */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Today's Calendar Events</h4>
          <div style={styles.eventList}>
            {calendarEvents.length === 0 ? (
              <p style={{fontSize: '0.9em', margin: 0}}>No Google Calendar events for today.</p>
            ) : (
              calendarEvents.map(event => (
                <div key={event.id} style={styles.eventItem}>
                  <strong>{event.summary}</strong>
                  <br/>
                  <small>
                    {event.start.dateTime ? 
                      format(new Date(event.start.dateTime), 'h:mm a') : 
                      'All-day'}
                  </small>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DayView;